import React from "react";
import { translations } from "../../utils/translations";

/**
 * ============================================================================
 * FooterLegal.jsx
 * ============================================================================
 *
 * RÔLE
 * ----------------------------------------------------------------------------
 * Footer légal compact et professionnel affiché en bas de l’application.
 *
 * OBJECTIF
 * ----------------------------------------------------------------------------
 * Donner un accès simple et stable aux pages légales et réglementaires :
 * - Mentions légales
 * - Politique de confidentialité
 * - Conditions TWINT
 *
 * COMPORTEMENT
 * ----------------------------------------------------------------------------
 * Desktop
 * - Coordonnées entreprise à gauche
 * - Liens légaux à droite
 *
 * Mobile
 * - Empilement vertical
 * - Boutons larges et faciles à cliquer
 * - Padding bas augmenté pour éviter la bottom nav mobile
 */

export default function FooterLegal({ setVueActuelle }) {
  const lang = (() => {
    const saved = localStorage.getItem("lang");
    return saved && ["fr", "de", "en"].includes(saved) ? saved : "fr";
  })();

  const t = translations[lang] || translations.fr;

  const goTo = (vue) => {
    if (typeof setVueActuelle !== "function") return;

    setVueActuelle(vue);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <footer className="footer-legal" aria-label="Informations légales">
        <div className="footer-legal-inner">
          <div className="footer-legal-company">
            <span className="footer-legal-name">
              {t.legalCompanyNameValue ||
                "La Maison du Fromage Neuchâtel - Sterchi Sàrl"}
            </span>

            <span className="footer-legal-sep">•</span>

            <span>rue du Trésor 2 bis</span>

            <span className="footer-legal-sep">•</span>

            <span>2000 Neuchâtel • Suisse</span>

            <span className="footer-legal-sep">•</span>

            <span>{t.legalPhoneValue || "+41 32 725 26 36"}</span>

            <span className="footer-legal-sep">•</span>

            <span>{t.legalEmailValue || "neuch@sterchi-fromages.ch"}</span>
          </div>

          <div className="footer-legal-links">
            <button
              type="button"
              onClick={() => goTo("mentions-legales")}
              aria-label={t.legalNotices || "Mentions légales"}
            >
              {t.legalNotices || "Mentions légales"}
            </button>

            <button
              type="button"
              onClick={() => goTo("politique-confidentialite")}
              aria-label={t.privacyPolicy || "Politique de confidentialité"}
            >
              {t.privacyPolicy || "Politique de confidentialité"}
            </button>

            <button
              type="button"
              onClick={() => goTo("twint-terms")}
              aria-label={t.twintTermsNav || "Conditions TWINT"}
            >
              {t.twintTermsNav || "Conditions TWINT"}
            </button>
          </div>
        </div>

        <style jsx="true">{`
          .footer-legal {
            position: relative;
            z-index: 999;
            margin-top: 48px;
            padding: 22px 20px 28px;
            background: #fbf8f1;
            border-top: 1px solid rgba(168, 138, 76, 0.12);
            font-size: 13px;
            color: #241c17;
          }
          .footer-legal * {
            visibility: visible;
            opacity: 1;
          }

          .footer-legal-company,
          .footer-legal-company span {
            color: rgba(36, 28, 23, 0.82);
          }

          .footer-legal-links button {
            position: relative;
            z-index: 1000;
          }

          .footer-legal-inner {
            max-width: 1100px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 20px;
            flex-wrap: wrap;
          }

          .footer-legal-company {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            color: rgba(75, 67, 61, 0.88);
            line-height: 1.6;
          }

          .footer-legal-name {
            font-weight: 700;
            color: #1f1a17;
            font-family: "Playfair Display", serif;
          }

          .footer-legal-sep {
            opacity: 0.35;
          }

          .footer-legal-links {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
          }

          .footer-legal-links button {
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
            font-size: 13px;
            font-weight: 700;
            color: #8c6a2f;
            line-height: 1.5;
          }

          .footer-legal-links button:hover {
            text-decoration: underline;
          }

          .footer-legal-links button:focus-visible {
            outline: 2px solid rgba(140, 106, 47, 0.32);
            outline-offset: 4px;
            border-radius: 6px;
          }

          @media (max-width: 768px) {
            .footer-legal {
              position: relative;
              z-index: 999;
              margin-top: 28px;
              padding: 16px 16px calc(62px + env(safe-area-inset-bottom));
              background: #fbf8f1;
            }

            .footer-legal-inner {
              flex-direction: column;
              align-items: flex-start;
              gap: 10px;
            }

            .footer-legal-company {
              flex-direction: column;
              gap: 2px;
              line-height: 1.45;
            }

            .footer-legal-sep {
              display: none;
            }

            .footer-legal-links {
              width: 100%;
              flex-direction: column;
              gap: 2px;
            }

            .footer-legal-links button {
              min-height: 32px;
              display: flex;
              align-items: center;
            }
          }
        `}</style>
      </footer>
    </>
  );
}
